import { useCallback } from "react";
import { useSnackbar } from "notistack";

import usePaginatedResource from "hooks/usePaginatedResource";
import { DEFAULT_PAGE_SIZE } from "config/pagination";

/**
 * Generic data + CRUD layer for a paginated entity table.
 *
 * Composes usePaginatedResource (loading / error / pagination) with an `api`
 * module; every mutation refreshes the list and reports via snackbar. Used by
 * useUsersTable and useWebsitesTable so the boilerplate lives in one place.
 *
 * @param {object}  api         { list, create, update, remove, activate, deactivate }
 *                              — pass a STABLE reference (module-level constant).
 * @param {string}  entityName  Singular label used in snackbar messages.
 * @param {number}  pageSize    Initial page size.
 */
export default function useEntityTable({ api, entityName = "Item", pageSize = DEFAULT_PAGE_SIZE }) {
  const { enqueueSnackbar } = useSnackbar();
  const lower = entityName.toLowerCase();

  const fetcher = useCallback((params) => api.list(params), [api]);
  const resource = usePaginatedResource(fetcher, { pageSize });
  const { refresh } = resource;

  const run = async (action, successMessage, failureMessage) => {
    try {
      const result = await action();
      enqueueSnackbar(successMessage, { variant: "success" });
      await refresh();
      return result;
    } catch (err) {
      enqueueSnackbar(err.normalizedMessage || failureMessage, { variant: "error" });
      throw err;
    }
  };

  return {
    ...resource,
    create: (values) =>
      run(() => api.create(values), `${entityName} created`, `Failed to create ${lower}`),
    update: (id, values) =>
      run(() => api.update(id, values), `${entityName} updated`, `Failed to update ${lower}`),
    remove: (id) => run(() => api.remove(id), `${entityName} deleted`, `Failed to delete ${lower}`),
    activate: (id) =>
      run(() => api.activate(id), `${entityName} activated`, `Failed to activate ${lower}`),
    deactivate: (id) =>
      run(() => api.deactivate(id), `${entityName} deactivated`, `Failed to deactivate ${lower}`),
  };
}
